import { Link } from 'react-router';

import { getVideoImage } from 'my-core/attachment-utils';
import { EDITOR_PLUGINS, getDefaultInstructorIntro } from 'my-core/course-instructor-utils';
import { approximateDuration } from 'my-core/date-utils';
import { hasContent } from 'my-core/editor-utils';
import { useDialogStateManager } from 'my-core/hooks';
import { getActivityTypeConfig } from 'my-core/online-course-activity-utils';
import { findChild, getComponentAtPath, getNext } from 'my-core/online-course-content-map';
import { studentCoursePageAssessmentRelativePath, studentCoursePageOnDemandRelativePath } from 'my-core/routes';
import { pluralize } from 'my-utils';

import InCourseExperienceHeader from 'my-components/OnlineCourse/InCourseExperience/InCourseExperienceHeader';
import SubscriptionCheckout from 'my-components/SubscriptionCheckout';
import Player, { PlayerLoader } from 'my-components/VideoPlayer';
import Editor, { LoadingFallback as EditorLoader } from 'my-editor';
import InstructorInfo from 'my-elements/InstructorInfo';
import LatexString from 'my-elements/LatexString';

import { findAssessmentWithContents, ON_DEMAND_MOCK_CONFIG } from './student-course-page-utils';
import StudentCoursePageClassRepApplicationBanner from './StudentCoursePageClassRepApplicationBanner';
import {
  GradeBoosterCardWrapper,
  GradeBoostersExplanation,
  GradeBoosterSubscriptionOffer,
  StudentCoursePageBookletCard,
  StudentCoursePageCard,
  StudentCoursePageContentsWrapper,
  StudentCoursePageFlashCardsDeckCard,
  StudentCoursePageSectionHeader,
  StudentCoursePageTwoColumnLayout,
} from './StudentCoursePageCommon';
import StudentCoursePageEmpty from './StudentCoursePageEmpty';
import StudentCoursePageGradeBoostersList from './StudentCoursePageGradeBoostersList';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import MuiLink from '@mui/material/Link';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';

import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { faEye, faForwardStep } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export default function StudentCoursePageOverview({
  assessments,
  classPass,
  classPassContentMap,
  classRepCourse,
  course,
  courseInstructor,
  courseSubscription,
  currentUser,
  drawerOpen,
  fetching,
  flashCardDecks,
  onlineCourses,
  onlineCourseUser,
  onToggleDrawer,
  studySessionBundles,
  studySessions,
  subscription,
  userCourse,
}) {
  const introVideo = courseInstructor?.video || classPass?.intro_video;
  const hasGradeBoosters = studySessions.length /* || onlineCourses.length ) */ > 0;
  const hasCourseContent = hasGradeBoosters || !!classPass;

  const [subscriptionCheckoutDialogOpen, subscriptionCheckoutDialogOpened, subscriptionCheckoutDialogActions] =
    useDialogStateManager();

  return (
    <>
      <InCourseExperienceHeader
        drawerOpen={drawerOpen}
        onToggleDrawer={onToggleDrawer}
        title={`${course?.code || ''} Overview`}
        titleVariant="h6"
      />
      <StudentCoursePageContentsWrapper>
        <Grid container spacing={{ xs: 2, md: 3 }} sx={{ '& > :empty': { display: 'none' } }}>
          {!fetching && !course.has_class_rep && !classRepCourse && subscription?.status !== 'active' && (
            <Grid size={12}>
              <StudentCoursePageClassRepApplicationBanner course={course} />
            </Grid>
          )}
          {(fetching || classPass || hasContent(courseInstructor?.instructor_introduction)) && (
            <Grid size={12}>
              <StudentCoursePageCard
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 3,
                  '& .videoWrapper': {
                    flex: '1 0 250px',
                    maxWidth: 400,
                    '& > div': {
                      borderRadius: 0.5,
                      overflow: 'hidden',
                    },
                  },
                  '& .content': {
                    flex: '2 1 200px',
                    minWidth: 250,
                    '& .contentActions': {
                      mt: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    },
                  },
                }}
              >
                {(introVideo || fetching) && (
                  <div className="videoWrapper">
                    {introVideo ?
                      <Player poster={getVideoImage(introVideo.urls.posters, 0)} urls={introVideo.urls} />
                    : fetching && <PlayerLoader />}
                  </div>
                )}
                <div className="content">
                  <Typography component="div">
                    {hasContent(courseInstructor?.instructor_introduction) ?
                      <Editor
                        fallback={<EditorLoader lines={4} />}
                        plugins={EDITOR_PLUGINS}
                        readOnly
                        value={courseInstructor?.instructor_introduction}
                      />
                    : fetching ?
                      <EditorLoader lines={4} />
                    : getDefaultInstructorIntro(course)}
                  </Typography>
                  <div className="contentActions">
                    {courseInstructor ?
                      <InstructorInfo instructor={courseInstructor.instructor} />
                    : <div />}
                    {/* <Button variant="contained">Ask a Question</Button> */}
                  </div>
                </div>
              </StudentCoursePageCard>
            </Grid>
          )}
          {!fetching && (
            <Grid size={12}>
              {hasCourseContent ?
                <StudentCoursePageTwoColumnLayout>
                  <div>
                    {classPass && (
                      <StudentCoursePageCurrentActivity
                        assessments={assessments}
                        classPassContentMap={classPassContentMap}
                        onlineCourseUser={onlineCourseUser}
                      />
                    )}
                    {classPass && (
                      <StudentCoursePageBookletCard
                        className="oneColumnVisible"
                        classPassId={classPass.id}
                        course={course}
                      />
                    )}
                    {hasGradeBoosters && (
                      <StudentCoursePageGradeBoostersList
                        assessments={assessments}
                        course={course}
                        courseSubscription={courseSubscription}
                        currentUser={currentUser}
                        onlineCourses={onlineCourses}
                        studySessionBundles={studySessionBundles}
                        studySessions={studySessions}
                        subscription={subscription}
                        userCourse={userCourse}
                      />
                    )}
                    <StudentCoursePageMockExamsCard classPassContentMap={classPassContentMap} />
                  </div>
                  <div>
                    {!!flashCardDecks?.length && (
                      <StudentCoursePageFlashCardsDeckCard course={course} flashCardDecks={flashCardDecks} />
                    )}
                    {classPass && (
                      <StudentCoursePageBookletCard
                        className="oneColumnHidden"
                        classPassId={classPass.id}
                        course={course}
                      />
                    )}
                    {!classPass && (
                      <>
                        <GradeBoostersExplanation />
                        {!subscription && (
                          <GradeBoosterSubscriptionOffer
                            onSubscribe={subscriptionCheckoutDialogActions.open}
                            studySessions={studySessions}
                          />
                        )}
                      </>
                    )}
                  </div>
                </StudentCoursePageTwoColumnLayout>
              : <StudentCoursePageEmpty course={course} userCourse={userCourse} />}
            </Grid>
          )}
        </Grid>
      </StudentCoursePageContentsWrapper>
      {subscriptionCheckoutDialogOpened && (
        <SubscriptionCheckout onClose={subscriptionCheckoutDialogActions.close} open={subscriptionCheckoutDialogOpen} />
      )}
    </>
  );
}

function StudentCoursePageCurrentActivity({ assessments, classPassContentMap, onlineCourseUser }) {
  const currentProg = onlineCourseUser?.progress?.current;
  const assessment = findAssessmentWithContents(assessments, currentProg?.assessment_id);
  if (!(assessment || classPassContentMap)) return null;
  const basePath =
    assessment ? studentCoursePageAssessmentRelativePath(assessment) : studentCoursePageOnDemandRelativePath();
  const contents = assessment?.contents || classPassContentMap;
  const currentNode =
    currentProg && findChild(contents, n => n.type === 'activity' && n.data.id === currentProg.activity_id);
  const nextNode =
    (currentNode ? getNext(currentNode, n => n.type === 'activity') : getNext(contents, n => n.type === 'activity')) ||
    currentNode;
  if (!nextNode) return null;
  return (
    <section>
      <StudentCoursePageSectionHeader title={currentNode ? 'Pick Up Where You Left Off' : 'Get Started'} />
      <StudentCoursePageCard>
        {currentNode && currentNode !== nextNode && (
          <>
            <Typography component="p" sx={{ mb: 1 }} variant="overline">
              <FontAwesomeIcon icon={faEye} /> Last Viewed
            </Typography>
            <Typography sx={{ mb: 2 }}>
              <MuiLink component={Link} to={`${basePath}${currentNode.fullPath}`} underline="hover">
                <LatexString>{`${currentNode.fullPos} ${currentNode.data.title} `}</LatexString>
              </MuiLink>
              {!currentNode.data.activity_metadata.reading_only && !!currentNode.data.activity_metadata.duration && (
                <span>
                  {approximateDuration(currentNode.data.activity_metadata.duration, { shortName: true }).join('')}
                </span>
              )}
            </Typography>
          </>
        )}
        <Typography component="p" sx={{ mb: 1 }} variant="overline">
          <FontAwesomeIcon icon={faForwardStep} /> Up Next
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {nextNode ?
              <img height={32} src={getActivityTypeConfig(nextNode.data).iconImg} width={32} />
            : <Skeleton height={32} variant="circular" width={32} />}
            <Typography sx={{ minWidth: 200 }}>
              <MuiLink component={Link} to={`${basePath}${nextNode?.fullPath}`} underline="hover">
                <LatexString>{`${nextNode?.fullPos} ${nextNode?.data?.title} `}</LatexString>
              </MuiLink>
              {!nextNode.data.activity_metadata.reading_only && !!nextNode.data.activity_metadata.duration && (
                <span>
                  {approximateDuration(nextNode.data.activity_metadata.duration, { shortName: true }).join('')}
                </span>
              )}
            </Typography>
          </Box>
          <Button
            component={Link}
            nativeButton={false}
            sx={{ ml: 'auto', flexShrink: 0 }}
            to={`${basePath}${nextNode?.fullPath}`}
            variant="contained"
          >
            {currentProg ? 'Continue' : 'Start Learning'}
          </Button>
        </Box>
      </StudentCoursePageCard>
    </section>
  );
}

function StudentCoursePageMockExamsCard({ classPassContentMap }) {
  if (!classPassContentMap) return null;
  const mockExams = getComponentAtPath(['mock-exam'], classPassContentMap).children;

  if (!mockExams.length) return null;

  return (
    <section>
      <StudentCoursePageSectionHeader title="Mock Exams" />
      <GradeBoosterCardWrapper
        image={ON_DEMAND_MOCK_CONFIG.image}
        imageBanner={ON_DEMAND_MOCK_CONFIG.banner}
        imageColor={ON_DEMAND_MOCK_CONFIG.color}
        title={<Typography variant="h6">On-Demand Mock Exams</Typography>}
      >
        <Typography>{ON_DEMAND_MOCK_CONFIG.description}</Typography>
        <div
          css={({ spacing, typography }) => ({
            ...typography.body2,
            display: 'flex',
            alignItems: 'center',
            columnGap: spacing(2),
          })}
        >
          {ON_DEMAND_MOCK_CONFIG.inclusions.map(({ label }) => (
            <span key={label}>
              <FontAwesomeIcon icon={faCheck} />
              {` ${label}`}
            </span>
          ))}
        </div>
        <div css={({ spacing }) => ({ display: 'flex', flexDirection: 'column', gap: spacing(1) })}>
          {mockExams.map(me => {
            const amd = me.data.activity_metadata;
            return (
              <div key={me.data.id}>
                <Typography variant="subtitle1">
                  <MuiLink
                    component={Link}
                    to={`../${studentCoursePageOnDemandRelativePath()}${me.fullPath}`}
                    underline="hover"
                  >
                    {me.data.title}
                  </MuiLink>
                </Typography>
                <Typography>
                  {[
                    amd.questions_count && pluralize(amd.questions_count, 'Question'),
                    amd.estimated_time &&
                      `~${pluralize(...approximateDuration(amd.estimated_time * 60, { excludeDays: true }))}`,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </Typography>
              </div>
            );
          })}
        </div>
      </GradeBoosterCardWrapper>
    </section>
  );
}
